import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import { AuthClient } from '@skytech/auth'
import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { InfoBox, Input, LanguageChoiceButtons, MessagePopup, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { COUNTRY_FLAGS, countryOptions, getDefaultCountryId } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import InfoOutlineIcon from 'mdi-react/InfoOutlineIcon'
import { useLocation, useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import useLogIn from '@/hooks/useLogin'
import authResources from '@/resources/auth'
import { APP_ROUTES } from '@/util/app-routes'

import AccessCodeView from './AccessCodeView'
import PasswordView from './PasswordView'
import queries from './queries'

import './Login.styles.scss'

type VisibleStep = 'phoneNumber' | 'password' | 'accessCode'

type FormFields = {
  phoneNumber: string
  phoneCountryId: number
  password?: string
  accessCode?: string
}

const Login: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const location = useLocation()
  const { whiteLabel, locale, setLocale } = useAppContext()

  const searchParams = new URLSearchParams(location.search)
  const nextUrl = searchParams.get('next') || APP_ROUTES.HOME
  // possible error msg coming from the azure integration
  const azureErrorMessage = searchParams.get('azureErrorMsg')

  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const [visibleStep, setVisibleStep] = useState<VisibleStep>('phoneNumber')
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  const loginMutation = useLogIn()
  const {
    data: loginSettings,
    isInitialLoading: isLoginSettingsLoading,
    refetch: fetchLoginSettings,
  } = useQuery(
    queries.loginSettings(),
    () => {
      if (!formRef.current) throw Error('Missing form values')
      return authResources.getSettings({
        phoneNumber: formRef.current?.values.phoneNumber,
        phoneCountryId: formRef.current?.values.phoneCountryId,
      })
    },
    {
      enabled: false,
      staleTime: Infinity,
      onSuccess({ isPasswordRequired, hasMsalLogin }) {
        if (hasMsalLogin) {
          const host = window.location.host
          window.location.replace(
            `${config.AUTH_API_URL}azure-msal/signin/manager?host=${host}&pathname=${nextUrl}`
          )
          return
        }

        setVisibleStep(isPasswordRequired ? 'password' : 'accessCode')
      },
      onError() {
        setErrorPopupMessage({
          text: translate('Something went wrong. Please refresh the page and try again.'),
          title: translate('Loading login settings error'),
        })
      },
    }
  )

  useEffect(() => {
    if (AuthClient.getToken()) {
      navigate('/')
    }
  }, [navigate])

  const handleSubmit = (values: FormFields) => {
    if (visibleStep === 'phoneNumber') {
      fetchLoginSettings()
      return
    }

    if (visibleStep === 'password' && loginSettings?.isAccessCodeRequired) {
      setVisibleStep('accessCode')
      return
    }

    loginMutation.mutate(values, {
      onSuccess() {
        navigate(nextUrl)
      },
      onError: () => {
        setErrorPopupMessage({
          text: translate('Login failed. Code incorrect or expired.'),
          title: translate('Login error'),
        })
      },
    })
  }

  const handleClickPrev = () => {
    if (visibleStep === 'password') {
      setVisibleStep('phoneNumber')
    }

    if (visibleStep === 'accessCode') {
      if (loginSettings?.isPasswordRequired) {
        setVisibleStep('password')
      } else {
        setVisibleStep('phoneNumber')
      }
    }
  }

  useEffect(() => {
    if (!azureErrorMessage) return

    setErrorPopupMessage({
      title: translate('Login attempt via Azure failed'),
      text: `${translate('Something went wrong when logging in, please try again!')} ${translate(
        'Error'
      )}: ${azureErrorMessage}`,
    })
  }, [navigate, azureErrorMessage, translate])

  const submitButtonText =
    (visibleStep === 'password' && !loginSettings?.isAccessCodeRequired) ||
    visibleStep === 'accessCode'
      ? translate('Log in')
      : translate('Continue')
  const isSubmitButtonLoading =
    visibleStep === 'phoneNumber' ? isLoginSettingsLoading : loginMutation.isLoading
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)
  const defaultPhoneCountryId = getDefaultCountryId(locale)

  return (
    <div className={`login-form-wrapper ${whiteLabel}`}>
      {environmentBadgeLabel && (
        <div className="development-info-container">
          <InfoBox emphasis="warning">
            <InfoOutlineIcon />
            <span className="development-text">
              <Translation
                id={
                  config.ENVIRONMENT === 'demo'
                    ? 'This is a demo version of the app.'
                    : 'This is a development version of the app.'
                }
              />
            </span>
          </InfoBox>
        </div>
      )}

      <div className="login-form">
        <div className="logo" />

        <div>
          <h1>
            <Translation id="Welcome to Manager!" />
          </h1>
        </div>

        <Formik
          innerRef={formRef}
          validateOnBlur={false}
          initialValues={{
            phoneNumber: '',
            phoneCountryId: defaultPhoneCountryId,
            password: '',
            accessCode: '',
          }}
          validate={values => {
            const errors: Partial<Record<keyof FormFields, string>> = {}

            if (!values.phoneNumber) {
              errors.phoneNumber = translate('This field is mandatory')
            }

            if (
              !values.password &&
              loginSettings?.isPasswordRequired &&
              visibleStep === 'password'
            ) {
              errors.password = translate('This field is mandatory')
            }

            if (
              !values.accessCode &&
              loginSettings?.isAccessCodeRequired &&
              visibleStep === 'accessCode'
            ) {
              errors.accessCode = translate('This field is mandatory')
            }

            return errors
          }}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <div className="flex gap-2">
                {visibleStep !== 'phoneNumber' && (
                  <div className="login-info">
                    <div>
                      <Translation
                        id="Logging in as {phoneNumber}"
                        phoneNumber={<span className="phone-number">{values.phoneNumber}</span>}
                      />
                    </div>
                  </div>
                )}

                {visibleStep === 'phoneNumber' && (
                  <>
                    <Field name="phoneCountryId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          id="phoneCountryId"
                          options={countryOptions.map(option => ({
                            value: option.value,
                            label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                          }))}
                          marginBottom={16}
                          {...field}
                          onChange={option => {
                            form.setFieldValue(field.name, option?.value)
                            // To preselect phone country code on the login page
                            localStorage.setItem('phoneCountryId', String(option?.value))
                          }}
                        />
                      )}
                    </Field>

                    <Field name="phoneNumber">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          id="phoneNumber"
                          wrapperClassName="grow"
                          ariaLabel={translate('Phone number')}
                          marginBottom={16}
                          touched={meta.touched}
                          error={meta.error}
                          placeholder={translate('Enter phone number')}
                          {...field}
                        />
                      )}
                    </Field>
                  </>
                )}
              </div>

              {visibleStep === 'password' && <PasswordView />}

              {visibleStep === 'accessCode' && (
                <AccessCodeView
                  phoneNumber={values.phoneNumber}
                  countryId={values.phoneCountryId}
                />
              )}

              <footer className="-mx-2 my-1 flex flex-col justify-between">
                <div
                  className={twJoin(
                    'mobile:flex-row flex flex-col',
                    visibleStep === 'phoneNumber' ? 'justify-end' : 'justify-between'
                  )}
                >
                  {visibleStep !== 'phoneNumber' && (
                    <Button size="medium" onClick={handleClickPrev} variant="tertiary">
                      <Translation id="Back" />
                    </Button>
                  )}

                  <Button
                    type="submit"
                    isIconRight
                    size="medium"
                    data-test="login-button"
                    Icon={<ArrowRightIcon />}
                    // No other ground on which loading could be shown when the redirection to the Azure platform happens
                    isLoading={isSubmitButtonLoading || loginSettings?.hasMsalLogin}
                  >
                    {submitButtonText}
                  </Button>
                </div>

                <LanguageChoiceButtons
                  locale={locale}
                  onChangeLocale={setLocale}
                  className="mobile:justify-end justify-evenly"
                />
              </footer>
            </Form>
          )}
        </Formik>

        <MessagePopup
          message={errorPopupMessage?.text}
          title={errorPopupMessage?.title}
          isOpen={Boolean(errorPopupMessage?.text)}
          onClose={() => setErrorPopupMessage(null)}
        />
      </div>
    </div>
  )
}

export default Login
